import { useCallback, useState } from 'react';

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

interface ParseSchema<TForm, TParsed> {
  parse: (values: TForm) => TParsed;
}

interface UseAnalysisWorkflowOptions<TForm extends { project: string; title: string }, TParsed, TResponse> {
  errors: Record<string, unknown>;
  values: TForm;
  schema: ParseSchema<TForm, TParsed>;
  analyze: (parsed: TParsed) => Promise<TResponse>;
  saveDataToDB: (project: string, title: string, inputs: TForm, results?: TResponse) => Promise<unknown>;
}

// Shared analyze/save workflow used by calculator pages.
// Pages still own their fields, charts, and print layouts; this hook only manages
// the repeated async state machine around schema parsing, API calls, and IndexedDB saves.
export function useAnalysisWorkflow<TForm extends { project: string; title: string }, TParsed, TResponse>({ errors, values, schema, analyze, saveDataToDB }: UseAnalysisWorkflowOptions<TForm, TParsed, TResponse>) {
  const [analysisResult, setAnalysisResult] = useState<TResponse | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const handleAnalyze = useCallback(async () => {
    if (Object.keys(errors).length > 0) {
      const errorFields = Object.keys(errors).join(', ');
      alert(`Please fix the following errors before analyzing:\n\n${errorFields}`);
      console.warn('Analyze blocked: fix errors first', errors);
      return;
    }

    setIsAnalyzing(true);
    try {
      const parsed = schema.parse(values);
      const result = await analyze(parsed);
      setAnalysisResult(result);
    } catch (err) {
      console.error('Analysis Error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  }, [analyze, errors, schema, values]);

  const handleSave = useCallback(async () => {
    if (Object.keys(errors).length > 0) {
      console.warn('Save blocked: fix errors first', errors);
      return;
    }

    setSaveStatus('saving');
    try {
      await saveDataToDB(values.project, values.title, values, analysisResult ?? undefined);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Save Error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  }, [analysisResult, errors, saveDataToDB, values]);

  return {
    analysisResult,
    setAnalysisResult,
    isAnalyzing,
    saveStatus,
    handleAnalyze,
    handleSave
  };
}
